import React, { useState, useEffect } from "react";
import { Wand2, RefreshCw, Film, Link as LinkIcon, Radar, Dna } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import CTAControlPanel from "../components/CTAControlPanel";
import PostCard from "../components/PostCard";
import SkeletonPost from "../components/SkeletonPost";
import BatchVideoGenerator from "../components/BatchVideoGenerator";
import CopyCatPanel from "../components/CopyCatPanel";
import StaticAdPanel from "../components/StaticAdPanel";
import { chatJSON } from "../lib/openai";
import { buildContentPrompt, enforceCtaInAllPosts } from "../lib/prompts";
import { gatherSignalsForNiche } from "../lib/trends";
import { toast } from "sonner";

export const CreateContent = ({ apiKey, offerData, handoff = null, clearHandoff = () => {} }) => {
  const [useOffer, setUseOffer] = useState(Boolean(offerData));
  const [niche, setNiche] = useState("");
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [audienceDNA, setAudienceDNA] = useState("");

  const [ctaText, setCtaText] = useState("");
  const [offer, setOffer] = useState("");
  const [autoCTA, setAutoCTA] = useState(true);

  const [stage, setStage] = useState("idle");
  const [posts, setPosts] = useState(null);
  const [signals, setSignals] = useState(null);
  const loading = stage !== "idle";
  const loadingLabel =
    stage === "trends"
      ? "Scanning Reddit + Google Trends + YouTube…"
      : stage === "generating"
      ? "Writing posts…"
      : "";

  // Hydrate from the asset-tab handoff (one-shot — clear after consuming).
  useEffect(() => {
    if (!handoff) return;
    if (handoff.niche)       setNiche(handoff.niche);
    if (handoff.product)     setProduct(handoff.product);
    if (handoff.audience)    setAudience(handoff.audience);
    if (handoff.audienceDNA) setAudienceDNA(handoff.audienceDNA);
    if (handoff.product && !offer) setOffer(handoff.product);
    setUseOffer(true);
    toast.success("Asset context applied — fields prefilled");
    clearHandoff();
  }, [handoff]); // eslint-disable-line

  useEffect(() => {
    if (useOffer && offerData) {
      const fallbackNiche =
        offerData.niche ||
        offerData.searching_for?.keywords?.[0] ||
        offerData.target_audience?.lifestyle ||
        offerData.digital_product?.format ||
        "";
      const fallbackProduct =
        offerData.digital_product?.idea ||
        offerData.offer_ladder?.low_ticket ||
        "";
      const fallbackAudience =
        offerData.target_audience?.who ||
        offerData.target_audience?.lifestyle ||
        "";
      setNiche(fallbackNiche);
      setProduct(fallbackProduct);
      setAudience(fallbackAudience);
      if (!offer) setOffer(fallbackProduct);
    }
  }, [useOffer, offerData]); // eslint-disable-line

  const generate = async () => {
    try {
      const query =
        niche || product || offer || "digital product creators";
      setStage("trends");
      const sigs = await gatherSignalsForNiche({ niche: query });
      setSignals(sigs);
      toast.message(
        `Pulled ${sigs.sources.reddit_count} Reddit${sigs.sources.youtube_count ? ` · ${sigs.sources.youtube_count} YouTube` : ""}${sigs.sources.google_count ? ` · ${sigs.sources.google_count} Google Trends` : ""}`
      );

      setStage("generating");
      const { system, user } = buildContentPrompt({
        ctaText,
        offer,
        autoCTA,
        niche,
        product,
        audience,
        audienceDNA,
        trendsDigest: sigs.digest,
      });
      const res = await chatJSON({ apiKey, system, user, temperature: 1.0 });
      if (!res.posts || !Array.isArray(res.posts)) {
        throw new Error("Response missing posts");
      }
      setPosts(enforceCtaInAllPosts(res.posts));
      toast.success("Content plan ready — grounded in live trends");
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setStage("idle");
    }
  };

  const updatePost = (i, updated) => {
    setPosts((prev) => prev.map((p, idx) => (idx === i ? updated : p)));
  };

  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-950 p-8 sm:p-12">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-pink-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-pink-300">
            <Film className="h-3 w-3" strokeWidth={2} />
            Content Engine
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-white max-w-3xl leading-[1.05]">
            Viral content for <span className="text-amber-400">your</span> offer.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-zinc-400 leading-relaxed">
            Pulls live Reddit + Google Trends + YouTube signal for your niche, mirrors how your audience actually talks, and gives you 4 editable, ready-to-post scripts grounded in real conversations.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-4">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <LinkIcon className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.8} />
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  Source
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="use-offer"
                  className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500"
                >
                  Use offer
                </Label>
                <Switch
                  id="use-offer"
                  checked={useOffer}
                  onCheckedChange={setUseOffer}
                  disabled={!offerData}
                  data-testid="use-offer-switch"
                  className="data-[state=checked]:bg-amber-500"
                />
              </div>
            </div>

            {!offerData && (
              <p className="text-[11px] text-zinc-500 mb-4">
                Tip: generate an asset in the <b className="text-zinc-300">Create Your Asset</b> tab first to auto-fill these fields.
              </p>
            )}

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Niche
                </Label>
                <Input
                  placeholder="e.g. gut health for women 35+"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  data-testid="content-niche-input"
                  className="h-10 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Product
                </Label>
                <Input
                  placeholder="e.g. 14-day gut reset protocol"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  data-testid="content-product-input"
                  className="h-10 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Audience
                </Label>
                <Input
                  placeholder="e.g. busy moms dealing with bloating"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="content-audience-input"
                  className="h-10 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-500"
                />
              </div>
              <div className="space-y-1.5 pt-1">
                <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-300/90 flex items-center gap-1.5">
                  <Dna className="h-3 w-3" strokeWidth={2.2} />
                  Audience DNA <span className="text-zinc-500 normal-case font-normal tracking-normal">· optional</span>
                </Label>
                <Textarea
                  placeholder={"Creators / channels / subs your audience already follows. e.g. @huberman, r/ADHD, @aliabdaal"}
                  value={audienceDNA}
                  onChange={(e) => setAudienceDNA(e.target.value)}
                  data-testid="content-audience-dna-input"
                  className="min-h-[64px] bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-emerald-500 text-[12.5px] leading-relaxed resize-none"
                />
              </div>
            </div>
          </div>

          <CTAControlPanel
            ctaText={ctaText}
            setCtaText={setCtaText}
            offer={offer}
            setOffer={setOffer}
            autoCTA={autoCTA}
            setAutoCTA={setAutoCTA}
            offerPlaceholder={product || "Your digital product"}
          />

          <Button
            onClick={generate}
            disabled={loading}
            data-testid="generate-content-button"
            className="w-full h-12 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 disabled:opacity-60"
          >
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} />
                {loadingLabel}
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-4 w-4" strokeWidth={2} />
                {posts ? "Re-Generate Content Plan" : "Generate Content Plan"}
              </>
            )}
          </Button>

          {signals && (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-[11px] text-emerald-200/90 flex items-center gap-2">
              <Radar className="h-3.5 w-3.5" strokeWidth={1.8} />
              {(signals.sources.reddit_count || signals.sources.youtube_count || signals.sources.google_count) ? (
                <>
                  Grounded in {signals.sources.reddit_count} Reddit
                  {signals.sources.youtube_count ? ` · ${signals.sources.youtube_count} YouTube` : ""}
                  {signals.sources.google_count ? ` · ${signals.sources.google_count} Google trends` : ""}
                </>
              ) : (
                <>Grounded in Market Research · Hook Library · Knowledge Base</>
              )}
            </div>
          )}

        </aside>

        <div className="lg:col-span-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-white">
              Your Content Plan
            </h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
              Educate · Entertain · Inspire · Sell
            </span>
          </div>

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[0, 1, 2, 3].map((i) => (
                <SkeletonPost key={i} />
              ))}
            </div>
          )}

          {!loading && !posts && (
            <div className="rounded-2xl border border-dashed border-white/10 bg-zinc-900/40 p-10 text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
                <Film className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <p className="mt-4 font-display text-lg text-white">
                Fill the fields and hit Generate.
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                Live trending data + your offer → 4 editable scripts with video prompts.
              </p>
            </div>
          )}

          {!loading && posts && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.map((p, i) => (
                  <PostCard
                    key={i}
                    post={p}
                    index={i}
                    apiKey={apiKey}
                    onChange={(updated) => updatePost(i, updated)}
                  />
                ))}
              </div>
              <StaticAdPanel
                apiKey={apiKey}
                niche={niche}
                audience={audience}
                offer={product || ""}
              />
              <BatchVideoGenerator posts={posts} apiKey={apiKey} />
              <CopyCatPanel
                apiKey={apiKey}
                ctaKeyword={(ctaText || "GO").trim().split(/\s+/)[0].toUpperCase()}
                niche={niche}
                audience={audience}
                offer={product || ""}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CreateContent;
